/**
 * Context Usage Indicator Component
 * Shows current conversation token usage vs model's context limit
 */

import { Badge, Group, Progress, Text, Tooltip } from '@mantine/core';
import { IconBrain } from '@tabler/icons-react';
import type React from 'react';

interface ContextUsageIndicatorProps {
  /** Current token count for the conversation */
  currentTokens: number;
  /** Maximum context window size for the model */
  contextLimit: number;
  /** Last response token count (optional, for additional info) */
  lastResponseTokens?: number;
  /** Whether to show compact view */
  compact?: boolean;
}

export const ContextUsageIndicator: React.FC<ContextUsageIndicatorProps> = ({
  currentTokens,
  contextLimit,
  lastResponseTokens,
  compact = false,
}) => {
  // Calculate usage percentage
  const usagePercentage = contextLimit > 0 ? (currentTokens / contextLimit) * 100 : 0;

  // Determine color based on usage percentage
  const getColor = (percentage: number) => {
    if (percentage < 60) return 'green';
    if (percentage < 80) return 'yellow';
    return 'red';
  };

  // Format numbers for display
  const formatTokens = (tokens: number): string => {
    if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(1)}M`;
    if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}k`;
    return tokens.toString();
  };

  const color = getColor(usagePercentage);

  // Create tooltip content
  const tooltipContent = (
    <div>
      <Text size="xs">
        <strong>Context Usage:</strong> {formatTokens(currentTokens)} / {formatTokens(contextLimit)}{' '}
        tokens ({usagePercentage.toFixed(1)}%)
      </Text>
      {lastResponseTokens !== undefined && lastResponseTokens > 0 && (
        <Text size="xs" mt={4}>
          <strong>Last Response:</strong> {formatTokens(lastResponseTokens)} tokens
        </Text>
      )}
      <Text size="xs" mt={4} c="dimmed">
        {usagePercentage < 60 && 'Good - plenty of context remaining'}
        {usagePercentage >= 60 &&
          usagePercentage < 80 &&
          'Moderate - consider starting new conversation soon'}
        {usagePercentage >= 80 && 'High - consider starting new conversation'}
      </Text>
    </div>
  );

  if (compact) {
    return (
      <Tooltip label={tooltipContent} withArrow>
        <Badge
          leftSection={<IconBrain size={12} />}
          color={color}
          variant="light"
          size="sm"
          style={{ cursor: 'help' }}
        >
          {usagePercentage.toFixed(0)}%
        </Badge>
      </Tooltip>
    );
  }

  return (
    <Tooltip label={tooltipContent} withArrow>
      <Group gap="xs" style={{ cursor: 'help', minWidth: 100 }}>
        <IconBrain size={16} color={`var(--mantine-color-${color}-6)`} />
        <div style={{ flex: 1 }}>
          <Group justify="space-between" gap="xs">
            <Text size="xs" c="dimmed">
              Context
            </Text>
            <Text size="xs" fw={500} c={color}>
              {formatTokens(currentTokens)}/{formatTokens(contextLimit)}
            </Text>
          </Group>
          <Progress
            value={usagePercentage}
            color={color}
            size="xs"
            mt={2}
            style={{ width: '100%' }}
          />
        </div>
      </Group>
    </Tooltip>
  );
};

export default ContextUsageIndicator;
